import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  EventUserEntity,
  useFindEventMatchResultsByUserQuery,
  useFindEventUsersQuery,
  useFindOneEventQuery,
} from 'src/store/eventApi';
import { Spinner } from 'src/ui/Spinner';
import PrintCard from './printCard';
import { assignSeats } from './seatNumbers';

const CJK_FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "微软雅黑", "Noto Sans CJK SC", "Source Han Sans SC", "WenQuanYi Micro Hei", sans-serif';

const PRINT_STYLES = `
.print-card, .print-card * { font-family: ${CJK_FONT_FAMILY}; }
@media print {
  body, html { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .print-page-shell { overflow: visible !important; height: auto !important; max-height: none !important; background: white !important; padding: 0 !important; }
  .print-card { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
}
`;

const PrintCardPage = () => {
  const { eventId, userId } = useParams<{ eventId: string; userId: string }>();
  const safeEventId = eventId ?? '';
  const safeUserId = userId ?? '';
  const skip = !safeEventId || !safeUserId;

  // Inject global print styles via a <style> tag — replaces @emotion/react Global
  useEffect(() => {
    const el = document.createElement('style');
    el.setAttribute('data-print-card-styles', '1');
    el.textContent = PRINT_STYLES;
    document.head.appendChild(el);
    return () => {
      document.head.removeChild(el);
    };
  }, []);

  const { data: event, isLoading: eventLoading } = useFindOneEventQuery(
    safeEventId,
    { skip }
  );
  const { data: attendees, isLoading: attendeesLoading } =
    useFindEventUsersQuery(safeEventId, { skip });
  const { data: matchData, isLoading: matchesLoading } =
    useFindEventMatchResultsByUserQuery(
      { eventId: safeEventId, userId: safeUserId },
      { skip }
    );

  const seatMap = useMemo(
    () => (attendees ? assignSeats(attendees) : new Map()),
    [attendees]
  );

  const attendeeMap = useMemo(() => {
    const m = new Map<string, EventUserEntity>();
    (attendees ?? []).forEach((eu) => m.set(eu.userId, eu));
    return m;
  }, [attendees]);

  const attendee = attendeeMap.get(safeUserId);
  const loading = eventLoading || attendeesLoading || matchesLoading;

  return (
    <div
      style={{
        height: '100vh',
        overflowY: 'auto',
        background: '#f7fafc',
        paddingTop: 24,
        paddingBottom: 24,
      }}
      className="print-page-shell"
    >
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingTop: 40,
            paddingBottom: 40,
            gap: 8,
          }}
        >
          <Spinner size="sm" />
          <span style={{ color: '#718096', fontSize: 14 }}>Loading…</span>
        </div>
      ) : !event ? (
        <span style={{ padding: '0 24px', color: '#718096' }}>
          Event not found.
        </span>
      ) : !attendee ? (
        <span style={{ padding: '0 24px', color: '#718096' }}>
          Attendee not found in this event.
        </span>
      ) : !matchData ? (
        <span style={{ padding: '0 24px', color: '#718096' }}>
          No match results available.
        </span>
      ) : (
        <PrintCard
          event={event}
          attendee={attendee}
          attendeeMap={attendeeMap}
          seatMap={seatMap}
          matches={matchData.matches}
        />
      )}
    </div>
  );
};

export default PrintCardPage;
